import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ActionIcon, Card, Stack, Group, Title, Text } from '@repletoryreach/sdk-ui';
import { Menu } from '@repletoryreach/sdk-ui/overlays';
import { StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { formatDate, formatInteger, formatRelative } from '../../../../lib/format';
import { ImpersonateButton } from './ImpersonateButton';
import { CopyableId } from '../../../../components/CopyableId';
import { TenantAvatar } from '../../components/TenantAvatar';
import type { admin } from '@repletoryreach/sdk-api';
import { useT } from '../../../../i18n/I18nProvider';

type Tenant = NonNullable<Awaited<ReturnType<typeof admin.listTenants>>['data']>[number];

const MUTED = 'var(--rr-color-text-muted)';

/**
 * The identity band at the top of a tenant: who this organisation is, the one
 * fact that decides whether anything works (active or not), the handful of
 * fields support quotes, and the single action worth putting first.
 */
export function TenantHeader({ tenant }: { tenant: Tenant }) {
  const t = useT();

  return (
    <Card withBorder>
      <Stack gap="md">
        <Link to="/tenants" style={{ color: MUTED, fontSize: 13, textDecoration: 'none', width: 'fit-content' }}>
          {t('tenants.view.header.backToAll')}
        </Link>

        <Group justify="space-between" align="flex-start" gap="md" wrap="wrap">
          <Group gap="md" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
            <TenantAvatar name={tenant.name} size="lg" />
            <Stack gap="xs" style={{ minWidth: 0 }}>
              <Group gap="sm" align="center" wrap="wrap">
                <Title order={1} style={{ fontSize: 28, margin: 0, lineHeight: 1.15 }}>{tenant.name}</Title>
                <StatusBadge size="sm" status={tenant.is_active ? 'healthy' : 'inactive'}>
                  {tenant.is_active ? t('status.active') : t('status.inactive')}
                </StatusBadge>
                {/* Self-service closure (2 October 2026). */}
                {tenant.closed_at ? (
                  <StatusBadge size="sm" status="inactive">{t('tenants.closing.closed', { date: formatDate(tenant.closed_at) })}</StatusBadge>
                ) : tenant.closes_at ? (
                  <StatusBadge size="sm" status="warning">{t('tenants.closing.closes', { date: formatDate(tenant.closes_at) })}</StatusBadge>
                ) : null}
                {tenant.refund_requested && !tenant.closed_at && <StatusBadge size="sm" status="pending">{t('tenants.closing.refund')}</StatusBadge>}
              </Group>
              <Text fz="sm" c={MUTED}>{tenant.slug}</Text>
            </Stack>
          </Group>

          <Group gap="sm" align="center">
            <ImpersonateButton tenant={tenant} />
            <TenantHeaderMenu tenant={tenant} />
          </Group>
        </Group>

        {/* The facts support quotes back, laid out as the design has them:
            label above value, wrapping into as many columns as fit. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
            gap: 16,
            paddingTop: 16,
            borderTop: '1px solid var(--rr-color-border)'
          }}
        >
          <Meta label={t('tenants.columns.country')} value={tenant.country_iso} />
          <Meta label={t('tenants.columns.timezone')} value={tenant.timezone} />
          <Meta label={t('tenants.columns.users')} value={formatInteger(tenant.user_count)} />
          <Meta
            label={t('common.created')}
            value={formatDate(tenant.created_at)}
            hint={formatRelative(tenant.created_at)}
          />
          {/* The tenant's name is the heading; the id stays only as something
              support can copy into a query or a ticket. */}
          <Meta
            label={t('tenants.view.header.tenantId')}
            value={<CopyableId value={tenant.id} label={t('tenantName.copyId')} chars={12} />}
          />
        </div>
      </Stack>
    </Card>
  );
}

function Meta({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <Text role="overline" c={MUTED}>{label}</Text>
      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2, minWidth: 0, overflow: 'hidden' }}>{value}</div>
      {hint && <Text role="caption" c={MUTED}>{hint}</Text>}
    </div>
  );
}

/**
 * The kebab beside the title. Everything in it that would change the tenant is
 * disabled: the lifecycle endpoints have not shipped, and a confirm dialog
 * that resolves to a "coming soon" toast is a trap dressed as a control.
 */
function TenantHeaderMenu({ tenant }: { tenant: Tenant }) {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const unavailable = t('tenants.row.lifecycleUnavailable');

  function openSettings() {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'settings');
    setSearchParams(params);
  }

  return (
    <Menu position="bottom-end" width={220}>
      <Menu.Target>
        <ActionIcon variant="subtle" size="md" aria-label={t('tenants.view.header.menuLabel', { name: tenant.name })}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
            <circle cx="8" cy="3" r="1.4" /><circle cx="8" cy="8" r="1.4" /><circle cx="8" cy="13" r="1.4" />
          </svg>
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={openSettings}>{t('tenants.view.header.openSettings')}</Menu.Item>
        <Menu.Divider />
        <span title={unavailable}>
          <Menu.Item disabled>
            {tenant.is_active ? t('tenants.row.suspend') : t('tenants.row.reactivate')}
          </Menu.Item>
        </span>
        <span title={unavailable}>
          <Menu.Item disabled color="error">{t('tenants.row.delete')}</Menu.Item>
        </span>
      </Menu.Dropdown>
    </Menu>
  );
}
